/* eslint-disable react/jsx-pascal-case */
import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';
import { Grid, Link } from '@mui/material';
import { TextField, Autocomplete, Button } from '@totalsoft/rocket-ui';
import { RightsContext } from 'providers/RightsContext';
import { Prompt } from 'react-router-dom';
import { getErrors, isValid } from '@totalsoft/pure-validations';
import { ParamsContext } from 'providers/ParamsContext';

const FolderGeneralInfoComponentAlpha = ({
  t,
  generalInfo,
  onGeneralInfoChange,
  onSave,
  savingGeneralInfo,
  isDirty,
  validation,
  responsiblesOptions,
  folderTypes,
  openLinkedDocument,
  isFacilityApproval,
  onCalculateApproval,
  riskAnalysisTypes,
  partnersOptions,
  isNewFolder
}) => {
  const { readOnlyFull, isImage, folderCalculateApproval, folderGeneralInfoWrite } = useContext(RightsContext);
  const { cvsChPercent } = useContext(ParamsContext);

  return (
    <>
      <Prompt when={isDirty} message={() => t('LeavingWithoutSaving')} />
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            name='folderResponsible'
            value={generalInfo.responsibleId}
            valueKey='responsibleId'
            labelKey='responsibleName'
            label={t('Folder.GeneralData.GeneralInfo.RiskResponsible')}
            options={responsiblesOptions}
            onChange={value => {
              onGeneralInfoChange({
                responsibleId: value,
                responsibleName: responsiblesOptions.find(x => x.responsibleId === value).responsibleName
              });
            }}
            disabled={readOnlyFull || !folderGeneralInfoWrite}
            isClearable={false}
            simpleValue
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            disabled
            fullWidth
            id={'folderGeneralInfo_partnerType'}
            name='folderClientType'
            value={generalInfo.client.clientTypeName || ''}
            label={t('Folder.GeneralData.GeneralInfo.PartnerType')}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <TextField
            disabled
            id={'folderGeneralInfo_partnerName'}
            fullWidth
            name='folderClientName'
            value={generalInfo.client.clientName || ''}
            error={!isValid(validation.clientId)}
            helperText={getErrors(validation.clientId)}
            label={t('Folder.GeneralData.GeneralInfo.PartnerName')}
          />
        </Grid>
        {isNewFolder && (
          <Grid item xs={12} md={6}>
            <Autocomplete
              fullWidth
              name='folderClientUniqueId'
              value={generalInfo.client.clientId}
              onChange={value => onGeneralInfoChange(value, 'client')}
              label={t('Folder.GeneralData.GeneralInfo.UniqueId')}
              valueKey='clientId'
              labelKey='uniqueId'
              options={partnersOptions}
              error={!isValid(validation.clientId)}
              helperText={getErrors(validation.clientId)}
            />
          </Grid>
        )}
        {generalInfo.client.partnerType?.isPF ? (
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              name='folderRelationType'
              id={'folderGeneralInfo_relationType'}
              value={generalInfo.client.relationType || ''}
              label={t('Folder.GeneralData.GeneralInfo.RelationType')}
              disabled={true}
            />
          </Grid>
        ) : (
          <Grid item xs={12} md={6}>
            <TextField
              disabled
              fullWidth
              id={'folderGeneralInfo_caenCode'}
              name='CaenCode'
              value={generalInfo.client.activityFieldCode || ''}
              label={t('Folder.GeneralData.GeneralInfo.CaenCode')}
            />
          </Grid>
        )}
        {generalInfo.client.partnerType?.isPF && (
          <Grid item xs={12} md={6}>
            <TextField
              id={'folderGeneralInfo_familyMembersNumber'}
              disabled={true}
              fullWidth
              value={generalInfo.client.familyMembersNumber}
              label={t('Folder.GeneralData.GeneralInfo.FamilyMembersNo')}
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <Autocomplete
            disabled={readOnlyFull || !folderGeneralInfoWrite}
            fullWidth
            value={generalInfo.folderTypeId}
            name='folderTypeId'
            valueKey='offerTypeId'
            labelKey='offerTypeName'
            label={t('Folder.GeneralData.GeneralInfo.FolderType')}
            options={folderTypes}
            onChange={value => {
              onGeneralInfoChange({ folderTypeId: value, folderType: folderTypes.find(x => x.offerTypeId === value).offerTypeName });
            }}
            isClearable={false}
            simpleValue={true}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth
            name='riskAnalysisType'
            value={generalInfo.riskAnalysisTypeId}
            valueKey='dictionaryItemId'
            simpleValue
            labelKey='dictionaryItemName'
            label={t('Folder.GeneralData.GeneralInfo.RiskAnalysisType')}
            options={riskAnalysisTypes}
            onChange={value => onGeneralInfoChange(value, 'riskAnalysisTypeId')}
            disabled={readOnlyFull || !folderGeneralInfoWrite}
            isClearable={false}
          />
        </Grid>
        {generalInfo.showRORAC && (
          <Grid item xs={12} md={6}>
            <TextField
              id={'folderGeneralInfo_rorac'}
              disabled={true}
              fullWidth
              isNumeric
              value={generalInfo.rorac}
              label={t('Folder.GeneralData.GeneralInfo.Rorac')}
            />
          </Grid>
        )}
        {generalInfo.showIIR && (
          <Grid item xs={12} md={6}>
            <TextField
              id={'folderGeneralInfo_iir'}
              disabled={true}
              fullWidth
              isNumeric
              value={generalInfo.internalInterestRate}
              label={t('Folder.GeneralData.GeneralInfo.IIR')}
              inputProps={{
                decimalScale: cvsChPercent
              }}
            />
          </Grid>
        )}
        {isFacilityApproval && generalInfo.renewedFacilityId && (
          <Grid item xs={12} md={6}>
            <TextField
              id={'folderGeneralInfo_RenewFacilityNo'}
              disabled={true}
              fullWidth
              label={t('Folder.GeneralData.GeneralInfo.RenewFacilityNo') + ' '}
              startAdornment={
                <Link
                  style={{ cursor: 'pointer' }}
                  onClick={openLinkedDocument(generalInfo.renewedFacilityId, generalInfo.renewedFacilitySiteId)}
                >
                  {generalInfo.renewedFacilityNumber}
                </Link>
              }
            />
          </Grid>
        )}
        <Grid item xs={12} md={6}>
          <TextField
            disabled={true}
            fullWidth
            id={'folderGeneralInfo_PartnerGroup'}
            name='folderPartnerGroup'
            label={t('Folder.GeneralData.GeneralInfo.PartnerGroup')}
            value={generalInfo.partnerGroup || ''}
          />
        </Grid>

        {!isImage && (
          <Grid item xs={10} mt={1}>
            <Button onClick={onSave} disabled={savingGeneralInfo || readOnlyFull || !folderGeneralInfoWrite || !isDirty}>
              {savingGeneralInfo ? t('Saving') + '...' : t('Save')}
            </Button>
          </Grid>
        )}
        {!isImage && (
          <Grid item xs={2} mt={1}>
            <Button
              color='primary'
              variant='contained'
              onClick={onCalculateApproval}
              id={'folderGeneralInfo_calculateApproval'}
              disabled={!generalInfo.canCalculateApproval || !folderCalculateApproval}
            >
              {t('Folder.GeneralData.GeneralInfo.CalculateApproval')}
            </Button>
          </Grid>
        )}
      </Grid>
    </>
  );
};

FolderGeneralInfoComponentAlpha.propTypes = {
  t: PropTypes.func.isRequired,
  generalInfo: PropTypes.object.isRequired,
  onGeneralInfoChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  savingGeneralInfo: PropTypes.bool,
  isDirty: PropTypes.bool.isRequired,
  validation: PropTypes.object,
  responsiblesOptions: PropTypes.array.isRequired,
  folderTypes: PropTypes.array,
  openLinkedDocument: PropTypes.func,
  isFacilityApproval: PropTypes.bool,
  onCalculateApproval: PropTypes.func,
  riskAnalysisTypes: PropTypes.array,
  partnersOptions: PropTypes.array.isRequired,
  isNewFolder: PropTypes.bool
};

export default withTranslation()(FolderGeneralInfoComponentAlpha);
